import type { IPagination } from "@common-shared/types/general.types"
import type { IPayerServicePayerMappingsBulkResponse } from "@common-shared/types/internalApis/payerService.types"
import type {
    IOVBulkUpsertPayerMappingsRequestData,
    IOVGetPayerMappingsRequestData,
    IOVUpsertPayerMappingRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types"
import { IOneVerifyResolvedPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"
import type { IOneVerifyPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper"

export class PayerMappingsService {
    /**
     * Get paginated list of payer mappings
     */
    static async getList(
        practice: IPractice,
        data: IOVGetPayerMappingsRequestData,
    ): Promise<
        | {
              items: IOneVerifyPayerMapping[]
              pagination: IPagination
          }
        | undefined
    > {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_GET_LIST.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Create payer mapping
     */
    static async createInstance(
        practice: IPractice,
        data: IOVUpsertPayerMappingRequestData,
    ): Promise<IOneVerifyPayerMapping | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_CREATE_INSTANCE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Update payer mapping
     */
    static async updateInstance(
        practice: IPractice,
        payerMappingId: number,
        data: IOVUpsertPayerMappingRequestData,
    ): Promise<IOneVerifyPayerMapping | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_UPDATE_INSTANCE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":mappingId", payerMappingId.toString()),
            data,
            "PUT",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Delete payer mapping
     */
    static async deleteInstance(
        practice: IPractice,
        payerMappingId: number,
    ): Promise<{ deletedId: number } | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_DELETE_INSTANCE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":mappingId", payerMappingId.toString()),
            undefined,
            "DELETE",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Validate bulk payer mappings upsert without writing
     */
    static async validateBulk(
        practice: IPractice,
        data: IOVBulkUpsertPayerMappingsRequestData,
    ): Promise<IPayerServicePayerMappingsBulkResponse | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_VALIDATE_BULK.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Bulk upsert payer mappings
     */
    static async upsertBulk(
        practice: IPractice,
        data: IOVBulkUpsertPayerMappingsRequestData,
    ): Promise<IPayerServicePayerMappingsBulkResponse | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_UPSERT_BULK.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Resolve a payer id through the practice's payer mappings.
     * payer is null when nothing mapped, so the caller falls back to the regular
     * payer lookup. undefined means the request itself failed.
     */
    static async resolvePayerMapping(
        practiceId: number,
        payerId: string,
    ): Promise<IOneVerifyResolvedPayerMapping | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.PAYER_MAPPINGS_RESOLVE.replace(
                ":practiceId",
                practiceId.toString(),
            ),
            { payerId },
            undefined,
            undefined,
            undefined,
            undefined,
            // A failed resolve is retried on the next csv row, so it must stay
            // quiet: toasting per row would flood the screen on a bad payer id
            // or while payer-service is unavailable. Gateway codes are listed
            // too - those answer with html rather than our envelope, which
            // HttpHelper reports as a generic failure
            [400, 500, 502, 503, 504],
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
